/* Import Review Page — staging'e alinmis bir batch'i incele, her satira karar
   ver (skip/import/force/avans), commit et veya iptal et.

   URL: /accounting/imports/:batchId
   Backend: routes/accounting/import_staging.py
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    AlertTriangle, ArrowLeft, Check, Loader2, ShieldAlert,
    SkipForward, Trash2, X, Filter as FilterIcon, CheckCircle2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMoney = (v) =>
    (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FLAG_META = {
    duplicate_invoice: { label: 'Duplicate Fatura', tone: 'amber', icon: ShieldAlert },
    overpayment: { label: 'Fazla Odeme', tone: 'amber', icon: AlertTriangle },
    fuzzy_company: { label: 'Benzer Firma', tone: 'amber', icon: AlertTriangle },
    weak_fuzzy_company: { label: 'Zayif Benzerlik', tone: 'sky', icon: AlertTriangle },
    alias_match: { label: 'Alias / Birlestirme', tone: 'sky', icon: AlertTriangle },
    unknown_company: { label: 'Bilinmeyen Firma', tone: 'sky', icon: AlertTriangle },
    missing_invoice_number: { label: 'Fatura No Eksik', tone: 'rose', icon: X },
    missing_date: { label: 'Tarih Eksik', tone: 'rose', icon: X },
    missing_total: { label: 'Tutar Eksik', tone: 'rose', icon: X },
    missing_amount: { label: 'Tutar Eksik', tone: 'rose', icon: X },
    duplicate_bank_tx: { label: 'Duplicate Banka Tx', tone: 'amber', icon: ShieldAlert },
    potential_double_payment: { label: 'Cift Odeme Suphesi', tone: 'amber', icon: AlertTriangle },
    unknown_counterparty: { label: 'Bilinmeyen Karsi Taraf', tone: 'sky', icon: AlertTriangle },
};

const TONE_CLASS = {
    amber: 'bg-amber-50 text-amber-900 border-amber-300',
    rose: 'bg-rose-50 text-rose-900 border-rose-300',
    sky: 'bg-sky-50 text-sky-900 border-sky-300',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-300',
};

const DECISION_OPTIONS = {
    invoices_xlsx: [
        { value: 'import', label: 'Iceri Aktar', tone: 'emerald' },
        { value: 'skip', label: 'Atla', tone: 'gray' },
        { value: 'import_force', label: 'Zorla Aktar (duplicate olsa bile)', tone: 'rose' },
        { value: 'import_as_advance', label: 'Avans Olarak Isle (overpayment)', tone: 'amber' },
    ],
    bank_xlsx: [
        { value: 'import', label: 'Iceri Aktar', tone: 'emerald' },
        { value: 'skip', label: 'Atla', tone: 'gray' },
        { value: 'import_force', label: 'Zorla Aktar (duplicate olsa bile)', tone: 'rose' },
    ],
    bank_csv: [
        { value: 'import', label: 'Iceri Aktar', tone: 'emerald' },
        { value: 'skip', label: 'Atla', tone: 'gray' },
        { value: 'import_force', label: 'Zorla Aktar (duplicate olsa bile)', tone: 'rose' },
    ],
};

function Badge({ tone = 'sky', children, testId }) {
    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${TONE_CLASS[tone] || TONE_CLASS.sky}`}
        >
            {children}
        </span>
    );
}

function FlagPill({ flag }) {
    const meta = FLAG_META[flag.code] || { label: flag.code, tone: 'sky', icon: AlertTriangle };
    const Icon = meta.icon;
    return (
        <Badge tone={meta.tone} testId={`flag-${flag.code}`}>
            <Icon size={11} /> {meta.label}
        </Badge>
    );
}

export default function ImportReviewPage() {
    const { batchId } = useParams();
    const navigate = useNavigate();
    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyIdx, setBusyIdx] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [filter, setFilter] = useState('all'); // all | pending | flagged | clean | error

    const fetchBatch = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/accounting/imports/staging/${batchId}`, { headers: hdrs() });
            setBatch(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Staging yuklenemedi');
            navigate('/accounting');
        } finally {
            setLoading(false);
        }
    }, [batchId, navigate]);

    useEffect(() => { fetchBatch(); }, [fetchBatch]);

    const setRowDecision = async (row, decision, payload = {}) => {
        setBusyIdx(row.idx);
        // optimistic
        setBatch((b) => {
            if (!b) return b;
            const rows = b.rows.map((r) => (r.idx === row.idx ? { ...r, decision, decision_payload: payload } : r));
            return { ...b, rows };
        });
        try {
            const r = await axios.patch(
                `${API}/api/accounting/imports/staging/${batchId}/rows/${row.idx}`,
                { decision, decision_payload: payload },
                { headers: hdrs() },
            );
            setBatch((b) => (b ? { ...b, stats: r.data.stats } : b));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Karar kaydedilemedi');
            fetchBatch();
        } finally {
            setBusyIdx(null);
        }
    };

    const bulkDecision = async (filterKey, decision) => {
        try {
            const r = await axios.patch(
                `${API}/api/accounting/imports/staging/${batchId}/bulk-decision`,
                { filter: filterKey, decision, decision_payload: {} },
                { headers: hdrs() },
            );
            toast.success(`${r.data.changed} satir guncellendi`);
            fetchBatch();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Toplu karar hatasi');
        }
    };

    const discard = async () => {
        if (!window.confirm('Bu yuklemeyi tamamen iptal et? (DB\'ye hicbir kayit yazilmadi)')) return;
        try {
            await axios.delete(`${API}/api/accounting/imports/staging/${batchId}`, { headers: hdrs() });
            toast.success('Yukleme iptal edildi');
            navigate('/accounting');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Iptal edilemedi');
        }
    };

    const commit = async (force = false) => {
        setCommitting(true);
        try {
            const r = await axios.post(
                `${API}/api/accounting/imports/staging/${batchId}/commit`,
                { force },
                { headers: hdrs() },
            );
            const d = r.data;
            toast.success(`${d.imported} eklendi · ${d.skipped} atlandi${d.error_count ? ` · ${d.error_count} hata` : ''}`,
                { duration: 8000 });
            navigate('/accounting');
        } catch (e) {
            const detail = e.response?.data?.detail || e.message;
            if (typeof detail === 'string' && detail.includes('karar bekliyor')) {
                if (window.confirm(`${detail}\n\nBekleyenleri ATLA ve devam et?`)) {
                    return commit(true);
                }
            } else {
                toast.error(detail);
            }
        } finally {
            setCommitting(false);
        }
    };

    const rows = batch?.rows || [];
    const filtered = useMemo(() => {
        if (filter === 'all') return rows;
        return rows.filter((r) => {
            const sev = new Set((r.flags || []).map((f) => f.severity));
            if (filter === 'pending') return r.decision === 'pending';
            if (filter === 'error') return sev.has('error');
            if (filter === 'flagged') return sev.has('warn');
            if (filter === 'clean') return !sev.has('warn') && !sev.has('error');
            return true;
        });
    }, [rows, filter]);

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center text-gray-500">
                <Loader2 className="animate-spin mr-2" /> Yukleniyor...
            </div>
        );
    }
    if (!batch) return null;

    const stats = batch.stats || {};
    const decisionOpts = DECISION_OPTIONS[batch.kind] || DECISION_OPTIONS.invoices_xlsx;
    const kindLabel = {
        invoices_xlsx: batch.side === 'purchase' ? 'Alis Faturalari (Excel)' : 'Satis Faturalari (Excel)',
        bank_xlsx: 'Banka Hareketleri (Excel)',
        bank_csv: 'Banka Hareketleri (CSV)',
    }[batch.kind] || batch.kind;

    return (
        <div className="min-h-screen bg-gray-50 pb-20" data-testid="import-review-page">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-10">
                <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/accounting')}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            data-testid="import-review-back"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <div>
                            <div className="text-xs text-gray-500">Import Onizleme & Onay</div>
                            <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                {kindLabel}
                                <span className="text-xs text-gray-400 font-normal">· {batch.filename}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={discard}
                            className="h-9 px-3 text-sm font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg flex items-center gap-1.5"
                            data-testid="import-review-discard"
                        >
                            <Trash2 size={14} /> Yuklemeyi Iptal Et
                        </button>
                        <button
                            onClick={() => commit(false)}
                            disabled={committing}
                            className="h-9 px-4 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg flex items-center gap-1.5"
                            data-testid="import-review-commit"
                        >
                            {committing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                            Secilenleri Iceri Aktar
                        </button>
                    </div>
                </div>

                {/* Stats bar */}
                <div className="max-w-[1400px] mx-auto px-6 pb-3 flex items-center gap-2 flex-wrap text-sm">
                    <Badge tone="sky">Toplam: <b className="ml-1">{stats.total || 0}</b></Badge>
                    <Badge tone="emerald">Temiz: <b className="ml-1">{stats.clean || 0}</b></Badge>
                    <Badge tone="amber">Suphe: <b className="ml-1">{stats.flagged || 0}</b></Badge>
                    <Badge tone="rose">Hata: <b className="ml-1">{stats.error || 0}</b></Badge>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-700">
                        Karar verilmis: <b>{stats.decided || 0}</b> / Bekleyen: <b className="text-amber-700">{stats.pending || 0}</b>
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={() => bulkDecision('flagged', 'skip')}
                            className="text-xs text-gray-600 hover:text-gray-900 underline"
                            data-testid="bulk-skip-flagged"
                        >
                            Tum suphelileri ATLA
                        </button>
                        <span className="text-gray-300">·</span>
                        <button
                            onClick={() => bulkDecision('pending', 'skip')}
                            className="text-xs text-gray-600 hover:text-gray-900 underline"
                            data-testid="bulk-skip-pending"
                        >
                            Bekleyenleri ATLA
                        </button>
                    </div>
                </div>

                {/* Filter tabs */}
                <div className="max-w-[1400px] mx-auto px-6 pb-2 flex items-center gap-1 text-xs">
                    <FilterIcon size={13} className="text-gray-400" />
                    {[
                        { k: 'all', l: `Tumu (${stats.total || 0})` },
                        { k: 'pending', l: `Bekleyen (${stats.pending || 0})` },
                        { k: 'flagged', l: `Suphe (${stats.flagged || 0})` },
                        { k: 'error', l: `Hata (${stats.error || 0})` },
                        { k: 'clean', l: `Temiz (${stats.clean || 0})` },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setFilter(f.k)}
                            data-testid={`filter-${f.k}`}
                            className={`px-3 py-1 rounded-md font-medium ${filter === f.k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                            {f.l}
                        </button>
                    ))}
                </div>
            </div>

            {/* Rows */}
            <div className="max-w-[1400px] mx-auto px-6 py-4">
                {filtered.length === 0 && (
                    <div className="text-center py-12 text-gray-400 text-sm">Bu filtrede satir yok.</div>
                )}
                <div className="space-y-2">
                    {filtered.map((row) => (
                        <RowCard
                            key={row.idx}
                            row={row}
                            kind={batch.kind}
                            decisionOpts={decisionOpts}
                            busy={busyIdx === row.idx}
                            onDecide={(d, p) => setRowDecision(row, d, p)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}


function RowCard({ row, kind, decisionOpts, busy, onDecide }) {
    const flags = row.flags || [];
    const sev = new Set(flags.map((f) => f.severity));
    const isClean = !sev.has('warn') && !sev.has('error');
    const isError = sev.has('error');

    const raw = row.raw || {};
    const isInvoice = kind === 'invoices_xlsx';

    const candidates = row.suggestions?.company_candidates || [];

    return (
        <div
            data-testid={`row-${row.idx}`}
            className={`bg-white border rounded-lg p-3 ${isError ? 'border-rose-300' : sev.has('warn') ? 'border-amber-300' : 'border-gray-200'}`}
        >
            <div className="flex items-start gap-3">
                <div className="text-xs text-gray-400 font-mono w-12 shrink-0 pt-1">#{row.src_row}</div>

                {/* Sol: Veri ozeti */}
                <div className="flex-1 min-w-0">
                    {isInvoice ? (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                            <span className="font-semibold text-gray-900">{raw.invoice_number || '—'}</span>
                            <Badge tone="sky">{raw.type}</Badge>
                            <span className="text-gray-600">{raw.invoice_date}</span>
                            <span className="text-gray-900">{raw.contact_name || '—'}</span>
                            {raw.vat_number && <span className="text-xs text-gray-500">VAT: {raw.vat_number}</span>}
                            <span className="ml-auto font-semibold text-gray-900">{fmtMoney(raw.total)} €</span>
                            {raw.paid_amount > 0 && (
                                <span className="text-xs text-emerald-700">odendi: {fmtMoney(raw.paid_amount)}</span>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                            <span className="text-gray-600">{raw.transaction_date}</span>
                            <span className={`font-semibold ${raw.amount < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                {raw.amount > 0 ? '+' : ''}{fmtMoney(raw.amount)} €
                            </span>
                            <span className="text-gray-900 truncate max-w-[260px]">{raw.counterparty_name || '—'}</span>
                            {raw.bank_ref && <span className="text-xs text-gray-400 font-mono">{raw.bank_ref}</span>}
                            {raw.communication && (
                                <span className="text-xs text-gray-500 truncate max-w-[300px]">{raw.communication}</span>
                            )}
                        </div>
                    )}

                    {/* Flags */}
                    {flags.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {flags.map((f, i) => <FlagPill key={i} flag={f} />)}
                        </div>
                    )}

                    {/* Detail messages */}
                    {flags.length > 0 && (
                        <div className="mt-1.5 text-xs text-gray-600 space-y-0.5">
                            {flags.map((f, i) => (
                                <div key={i}>· {f.message}</div>
                            ))}
                        </div>
                    )}

                    {/* Company candidates (fuzzy / alias) */}
                    {isInvoice && candidates.length > 0 && (sev.has('warn') || flags.some((f) => f.code === 'unknown_company')) && (
                        <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
                            <span className="text-gray-500">Olasi firma:</span>
                            {candidates.slice(0, 4).map((c) => (
                                <button
                                    key={c.id}
                                    data-testid={`row-${row.idx}-candidate-${c.id}`}
                                    onClick={() => onDecide('import', { company_id: c.id })}
                                    className={`px-2 py-1 rounded border text-xs ${row.decision_payload?.company_id === c.id
                                        ? 'bg-emerald-600 text-white border-emerald-700'
                                        : 'bg-gray-50 text-gray-800 border-gray-200 hover:bg-gray-100'}`}
                                >
                                    {c.name}
                                    <span className="ml-1 text-[10px] opacity-70">%{Math.round(c.score * 100)}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Sag: karar */}
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <div className="flex items-center gap-1">
                        {decisionOpts.map((opt) => {
                            const active = row.decision === opt.value;
                            // Bazi kararlar sadece ilgili flag varken anlamli
                            const showAdvance = opt.value === 'import_as_advance' && flags.some((f) => f.code === 'overpayment');
                            const showForce = opt.value === 'import_force' && flags.some((f) =>
                                f.code === 'duplicate_invoice' || f.code === 'duplicate_bank_tx');
                            const isBasic = opt.value === 'import' || opt.value === 'skip';
                            if (!isBasic && !showAdvance && !showForce) return null;

                            const baseCls = 'px-2.5 py-1 rounded-md text-xs font-medium border transition-colors flex items-center gap-1';
                            const toneCls = active
                                ? (opt.tone === 'emerald'
                                    ? 'bg-emerald-600 text-white border-emerald-700'
                                    : opt.tone === 'rose'
                                        ? 'bg-rose-600 text-white border-rose-700'
                                        : opt.tone === 'amber'
                                            ? 'bg-amber-500 text-white border-amber-600'
                                            : 'bg-gray-700 text-white border-gray-800')
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50';
                            const Icon = opt.value === 'skip' ? SkipForward
                                : opt.value === 'import_force' ? ShieldAlert
                                : opt.value === 'import_as_advance' ? AlertTriangle
                                : Check;
                            return (
                                <button
                                    key={opt.value}
                                    disabled={busy}
                                    onClick={() => onDecide(opt.value, {})}
                                    className={`${baseCls} ${toneCls}`}
                                    data-testid={`row-${row.idx}-decision-${opt.value}`}
                                    title={opt.label}
                                >
                                    <Icon size={12} />
                                    {opt.label.split(' ')[0]}
                                </button>
                            );
                        })}
                    </div>
                    {row.decision === 'pending' && (
                        <div className="text-[10px] text-amber-700 font-semibold">KARAR BEKLIYOR</div>
                    )}
                    {row.decision_payload?.company_id && (
                        <div className="text-[10px] text-emerald-700">→ secili firma kullanilacak</div>
                    )}
                    {isClean && row.decision === 'import' && (
                        <div className="text-[10px] text-emerald-700">auto-decide</div>
                    )}
                </div>
            </div>
        </div>
    );
}
